import MenuLogo from '@/components/common/MenuLogo'
import useResponsive from '@/hooks/useResponsive'
import useEmblaCarousel from 'embla-carousel-react'
import { cn } from 'oddle-shop-monorepo.design-system'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import ImageWithLoading from '@/components/common/ImageWithLoading'

interface ProductImageCarouselProps {
  images: {
    imageUrl: string
    blurDataURL: string | undefined
  }[]
}

const ProductImageCarousel: React.FC<ProductImageCarouselProps> = ({
  images,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [mainViewportRef, mainApi] = useEmblaCarousel({
    loop: true,
    containScroll: 'trimSnaps',
  })

  const [thumbViewportRef, thumbApi] = useEmblaCarousel({
    containScroll: 'keepSnaps',
    dragFree: true,
  })

  const { desktop } = useResponsive()

  // Use ref to store the latest onSlideChange callback to avoid dependency issues
  const onSlideChangeRef = useRef<((index: number) => void) | null>(null)
  onSlideChangeRef.current = (index: number) => setSelectedIndex(index)

  const onThumbClick = useCallback(
    (index: number) => {
      if (!mainApi || !thumbApi) return
      mainApi.scrollTo(index)
    },
    [mainApi, thumbApi]
  )

  const onSelect = useCallback(() => {
    if (!mainApi || !thumbApi) return
    const newIndex = mainApi.selectedScrollSnap()
    setSelectedIndex(newIndex)
    thumbApi.scrollTo(newIndex)
  }, [mainApi, thumbApi])

  useEffect(() => {
    if (!mainApi) return

    // Initial setup
    onSelect()

    // Add event listeners
    mainApi.on('select', onSelect)
    mainApi.on('reInit', onSelect)

    return () => {
      mainApi.off('select', onSelect)
      mainApi.off('reInit', onSelect)
    }
  }, [mainApi, onSelect])

  useEffect(() => {
    if (!thumbApi) return

    // Sync thumbnail carousel with main carousel
    thumbApi.scrollTo(selectedIndex)
  }, [thumbApi, selectedIndex])

  return (
    <div className="w-full">
      {/* Main Image Carousel */}
      <div className="overflow-hidden md:rounded-2xl" ref={mainViewportRef}>
        <div className="flex">
          {images.map((image, index) => (
            <div
              key={image.imageUrl}
              className="flex flex-[0_0_100%] items-center"
            >
              {image ? (
                <div className="flex h-full w-screen items-center justify-center bg-[#F5F8FC] md:h-[360px] md:w-[360px] md:rounded-2xl">
                  {!images?.[0]?.imageUrl?.includes('default-product.png') ? (
                    <ImageWithLoading
                      src={image.imageUrl}
                      alt="product-image"
                      sizes={desktop ? '360px' : '100vw'}
                      width={360}
                      height={360}
                      className="mx-auto h-auto max-h-[100vw] w-full max-w-full object-contain lg:h-auto lg:max-h-[360px] lg:w-full lg:max-w-[360px]"
                      priority={index === 0}
                      blurDataURL={image.blurDataURL}
                    />
                  ) : (
                    <div className="-ml-3.5 flex h-[360px] items-center justify-center opacity-20">
                      <MenuLogo showStoreName={false} />
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </div>

      {/* Thumbnail Navigation */}
      {images.length > 1 && (
        <div className="mt-4 overflow-hidden" ref={thumbViewportRef}>
          <div className="flex gap-2.5">
            {images.map((image, index) => (
              <div
                key={image.imageUrl}
                className={cn(
                  'flex-[0_0_144px] cursor-pointer rounded-lg border-2 transition-all duration-200',
                  selectedIndex === index
                    ? 'border-primary'
                    : 'border-transparent hover:border-gray-300'
                )}
                onClick={() => onThumbClick(index)}
              >
                {image ? (
                  <ImageWithLoading
                    src={image.imageUrl}
                    alt="product-image-thumbnail"
                    width={144}
                    height={60}
                    className="h-[60px] w-full rounded-lg object-cover"
                    blurDataURL={image.blurDataURL}
                    blurImageClassName="rounded-lg"
                  />
                ) : null}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default ProductImageCarousel
